import { useMemo, useState } from 'react';
import classNames from 'classnames';
import Accordion from 'components/gruenland/atoms/accordion';
import Button from 'components/gruenland/atoms/button/Button';
import FeedbackIconLayer from 'components/gruenland/atoms/button/feedbackIconLayer';
import Drawer from 'components/gruenland/atoms/drawer/Drawer';
import FilterCount from 'components/gruenland/atoms/filters/FilterCount';
import { ChevronDown } from 'components/gruenland/icons/ChevronDown';
import { Close } from 'components/gruenland/icons/Close';
import { useFormat } from 'helpers/hooks/useFormat';
import { TYPOGRAPHY_PLP_COUNT_LABEL } from 'lib/typography';
import SortFacets from './SortFacets';
import { useProductList } from '../../context';
import useDynamicFacets from '../../hooks/useDynamicFacets';

const accordionClassNames = {
  root: 'py-5 px-4',
  icon: 'stroke-secondary-black w-5 stroke-2',
  border: 'border-b border-neutral-400',
  button: 'text-[14px]',
};

const Facets: React.FC = () => {
  const { formatMessage } = useFormat({ name: 'gruenland' });

  const { activeRefinements, navigating, removeAllRefinements, facetsConfiguration, totalItems } = useProductList();

  const [isOpen, setIsOpen] = useState(false);

  const renderFacet = useMemo<NonNullable<Parameters<typeof useDynamicFacets>[0]>['render']>(
    () =>
      // eslint-disable-next-line react/display-name
      ({ attribute, Component }) => (
        <Accordion
          key={attribute}
          className={`${accordionClassNames.root} ${accordionClassNames.border}`}
          buttonClassName={accordionClassNames.button}
          closedSectionTitle={facetsConfiguration[attribute].label}
          variant="arrow"
          iconClassName={accordionClassNames.icon}
        >
          <div className="px-4 py-7">{Component}</div>
        </Accordion>
      ),
    [facetsConfiguration],
  );

  const facets = useDynamicFacets({
    configuration: facetsConfiguration,
    render: renderFacet,
  });

  return (
    <>
      <div className="border-b border-neutral-400 pb-4 pt-12">
        <span className={classNames(TYPOGRAPHY_PLP_COUNT_LABEL, 'uppercase')}>
          {totalItems} {formatMessage({ id: 'plp.products', defaultMessage: 'Prodotti' })}
        </span>
      </div>
      <div className="flex items-center gap-5 py-8">
        <button className="flex items-center gap-2" onClick={() => setIsOpen(true)}>
          <span className="text-[14px]">{formatMessage({ id: 'plp.filters', defaultMessage: 'Filtri' })}</span>
          {activeRefinements.length > 0 && <FilterCount>{activeRefinements.length}</FilterCount>}
          <ChevronDown className="w-5" />
        </button>
        <SortFacets />
      </div>
      <Drawer isOpen={isOpen} onClose={() => setIsOpen(false)} direction="left" className="w-[90%] max-w-[400px]">
        <div className="relative flex items-center justify-between border-b border-neutral-400 px-3 py-4">
          <h3 className="text-[24px]/[32px] font-medium">
            {formatMessage({ id: 'plp.filtersDrawer.title', defaultMessage: 'Filtra prodotti' })}
          </h3>
          <button onClick={() => setIsOpen(false)}>
            <Close />
          </button>
        </div>
        <div className="grow overflow-auto">{facets}</div>

        <div className="flex w-full items-center gap-[18px] border-t bg-white px-[14px] py-[18px]">
          <Button
            variant="buttonSecondary"
            onClick={() => {
              removeAllRefinements();
              setIsOpen(false);
            }}
            className="w-full"
          >
            {formatMessage({ id: 'plp.clearAll', defaultMessage: 'Azzera' })}
          </Button>
          <Button variant="buttonPrimaryBlack" className="w-full" onClick={() => setIsOpen(false)}>
            {formatMessage({ id: 'plp.showResults', defaultMessage: 'Mostra risultati' })}
          </Button>
          {navigating && <FeedbackIconLayer className="z-50 backdrop-opacity-50" loading />}
        </div>
      </Drawer>
    </>
  );
};

export default Facets;
